import React, { useEffect, useState } from "react";
import { Divider, ToggleButton, ToggleButtonGroup } from "@mui/material";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import { useSearchParams } from "react-router-dom";
import GlobalRolePage from "../GlobalRole/GlobalRolePage";
import ClientRolePage from "../ClientRole/ClientRolePage";
import ProjectRolePage from "../ProjectRole/ProjectRolePage";
import ManagementPage from "../shared/ManagementPage";

const ROLE_SCOPES = Object.freeze({
  global: "global",
  client: "client",
  project: "project",
});

const scopeToggleSx = {
  display: "flex",
  flexWrap: "wrap",
  gap: 1,
  width: "fit-content",
  maxWidth: "100%",
  mx: "auto",
  "& .MuiToggleButtonGroup-grouped": {
    border: "1px solid rgba(255,255,255,0.18)",
    borderRadius: "999px !important",
    mx: 0,
    flex: "0 0 auto",
  },
  "& .MuiToggleButton-root": {
    flex: "0 0 auto",
    minWidth: { xs: 120, sm: 150 },
    textTransform: "none",
    fontSize: { xs: 12, sm: 13 },
    fontWeight: 600,
    py: 0.85,
    px: { xs: 2, sm: 3 },
    whiteSpace: "nowrap",
    color: "rgba(255,255,255,0.65)",
    background: "rgba(255,255,255,0.04)",
    borderRadius: "999px",
    transition: "all 0.24s ease",
    "&.Mui-selected": {
      color: "#fff",
      background:
        "linear-gradient(135deg, rgba(255,255,255,0.36), rgba(255,255,255,0.2))",
      borderColor: "rgba(255,255,255,0.45)",
      boxShadow:
        "inset 0 1px 0 rgba(255,255,255,0.35), 0 0 0 3px rgba(255,255,255,0.08)",
      "&:hover": {
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.4), rgba(255,255,255,0.24))",
      },
    },
    "&:hover": {
      background: "rgba(255,255,255,0.08)",
    },
  },
};

export default function RolePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const scopeParam = searchParams.get("scope");
  const initialScope = ROLE_SCOPES[scopeParam] ? scopeParam : ROLE_SCOPES.global;
  const [roleScope, setRoleScope] = useState(initialScope);

  useEffect(() => {
    if (ROLE_SCOPES[scopeParam] && scopeParam !== roleScope) {
      setRoleScope(scopeParam);
    }
  }, [scopeParam, roleScope]);

  const handleScopeChange = (_, value) => {
    if (!value) return;
    setRoleScope(value);
    setSearchParams({ scope: value }, { replace: true });
  };

  return (
    <ManagementPage
      title="Role"
      icon={<BadgeOutlinedIcon />}
      toolbar={
        <>
          <ToggleButtonGroup
            exclusive
            value={roleScope}
            onChange={handleScopeChange}
            sx={{ ...scopeToggleSx, flexShrink: 0 }}
          >
            <ToggleButton value={ROLE_SCOPES.global}>
              <BadgeOutlinedIcon sx={{ fontSize: 16, mr: 0.75 }} />
              Global Level Role
            </ToggleButton>
            <ToggleButton value={ROLE_SCOPES.client}>
              <WorkOutlineIcon sx={{ fontSize: 16, mr: 0.75 }} />
              Client Level Role
            </ToggleButton>
            <ToggleButton value={ROLE_SCOPES.project}>
              <FolderOutlinedIcon sx={{ fontSize: 16, mr: 0.75 }} />
              Project Level Role
            </ToggleButton>
          </ToggleButtonGroup>

          <Divider sx={{ my: 1.5, borderColor: "rgba(255,255,255,0.1)", flexShrink: 0 }} />
        </>
      }
    >
      {roleScope === ROLE_SCOPES.global && <GlobalRolePage embedded />}
      {roleScope === ROLE_SCOPES.client && <ClientRolePage embedded />}
      {roleScope === ROLE_SCOPES.project && <ProjectRolePage embedded />}
    </ManagementPage>
  );
}
